/**
 * CostsPage - Phase 5 Enhanced Cost Management
 * Comprehensive cost analysis and optimization dashboard
 * 
 * @description Professional cost management interface featuring:
 * - Multi-cloud cost tracking and analysis
 * - Budget management and alerts
 * - Cost optimization recommendations
 * - Billing forecasting and trends
 * - Resource cost allocation and tagging
 * - Cost anomaly detection
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Download,
  Settings,
  Calendar,
  Target,
  PiggyBank,
  Zap
} from "lucide-react";

// Mock cost data
const monthlyCosts = [
  { month: 'Jul', aws: 1200, gcp: 800, azure: 600, total: 2600 },
  { month: 'Aug', aws: 1350, gcp: 750, azure: 650, total: 2750 },
  { month: 'Sep', aws: 1180, gcp: 820, azure: 580, total: 2580 },
  { month: 'Oct', aws: 1420, gcp: 900, azure: 720, total: 3040 },
  { month: 'Nov', aws: 1380, gcp: 880, azure: 690, total: 2950 },
  { month: 'Dec', aws: 1247, gcp: 842, azure: 635, total: 2724 }
];

const serviceCosts = [
  { name: 'Compute', value: 1245, color: '#3b82f6' },
  { name: 'Storage', value: 687, color: '#10b981' },
  { name: 'Network', value: 421, color: '#f59e0b' },
  { name: 'Database', value: 298, color: '#ef4444' },
  { name: 'Other', value: 73, color: '#8b5cf6' }
];

const budgets = [
  { name: 'Production Environment', budget: 2000, spent: 1647, percentage: 82 },
  { name: 'Development Environment', budget: 500, spent: 423, percentage: 85 },
  { name: 'Testing Environment', budget: 300, spent: 187, percentage: 62 }
];

export default function CostsPage() {
  const [selectedTimeRange, setSelectedTimeRange] = useState("6m");
  
  const currentMonthCost = 2724;
  const previousMonthCost = 2950;
  const costChange = ((currentMonthCost - previousMonthCost) / previousMonthCost) * 100;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Cost Management</h1>
          <p className="text-muted-foreground">
            Monitor and optimize your multi-cloud spending
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Billing Period
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Cost Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold text-foreground">${currentMonthCost.toLocaleString()}</p>
                <p className={`text-xs flex items-center mt-1 ${
                  costChange < 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {costChange < 0 ? <TrendingDown className="w-3 h-3 mr-1" /> : <TrendingUp className="w-3 h-3 mr-1" />}
                  {Math.abs(costChange).toFixed(1)}% from last month
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Budget Usage</p>
                <p className="text-2xl font-bold text-foreground">78%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  $2,257 of $2,900 budget
                </p>
              </div>
              <Target className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Projected</p>
                <p className="text-2xl font-bold text-foreground">$2,890</p>
                <p className="text-xs text-yellow-600 flex items-center mt-1">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Near budget limit
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Savings</p>
                <p className="text-2xl font-bold text-foreground">$342</p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <PiggyBank className="w-3 h-3 mr-1" />
                  This month
                </p>
              </div>
              <Zap className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Cost Trend</span>
              <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3m">Last 3 months</SelectItem>
                  <SelectItem value="6m">Last 6 months</SelectItem>
                  <SelectItem value="12m">Last 12 months</SelectItem>
                </SelectContent>
              </Select>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyCosts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`$${value}`, '']}
                />
                <Area 
                  type="monotone" 
                  dataKey="aws" 
                  stackId="1"
                  stroke="#ff9500" 
                  fill="#ff9500"
                  fillOpacity={0.6}
                />
                <Area 
                  type="monotone" 
                  dataKey="gcp" 
                  stackId="1"
                  stroke="#4285f4" 
                  fill="#4285f4"
                  fillOpacity={0.6}
                />
                <Area 
                  type="monotone" 
                  dataKey="azure" 
                  stackId="1"
                  stroke="#00bcf2" 
                  fill="#00bcf2"
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Service Cost Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Service Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={serviceCosts}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }: any) => `${name}: $${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {serviceCosts.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${value}`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Budget Management */}
      <Card>
        <CardHeader>
          <CardTitle>Budget Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {budgets.map((budget, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-foreground">{budget.name}</h4>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      ${budget.spent.toLocaleString()} / ${budget.budget.toLocaleString()}
                    </p>
                    <Badge className={
                      budget.percentage >= 90 ? 'bg-red-100 text-red-800' :
                      budget.percentage >= 75 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }>
                      {budget.percentage}% used
                    </Badge>
                  </div>
                </div>
                <Progress value={budget.percentage} className="h-3" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Remaining: ${(budget.budget - budget.spent).toLocaleString()}</span>
                  <span>{budget.percentage >= 90 ? 'Over budget risk' : 'On track'}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cost Optimization Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Optimization Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-100">Reserved Instances</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                    Switch to reserved instances for production workloads
                  </p>
                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                    Potential savings: $180/month
                  </Badge>
                </div>
              </div>
            </div>

            <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-950">
              <div className="flex items-start gap-3">
                <PiggyBank className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-900 dark:text-green-100">Unused Resources</h4>
                  <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                    3 idle instances detected in staging environment
                  </p>
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200">
                    Potential savings: $95/month
                  </Badge>
                </div>
              </div>
            </div>

            <div className="p-4 border rounded-lg bg-purple-50 dark:bg-purple-950">
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-900 dark:text-purple-100">Right-sizing</h4>
                  <p className="text-sm text-purple-700 dark:text-purple-300 mb-2">
                    Over-provisioned instances in development environment
                  </p>
                  <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
                    Potential savings: $67/month
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}